<template>
  <div ref="container" style="width: 100%; display: flex; flex: 1 1 auto; flex-direction: column">
    <v-progress-circular
      v-if="loadingData"
      :width="15"
      :size="350"
      :value="loadingValue"
      color="primary"
      style="align-self: center; margin-bottom: auto; margin-top: auto"
    >
      <span class="headline">{{ $t('Pilot.Graphics.LoadingData') }}</span>
    </v-progress-circular>
    <template v-else-if="graphesToPlot.length > 0">
      <v-layout row justify-center style="flex: 0 0 auto">
        <v-btn
          small
          :color="dataLimitMode === dataLimit ? 'secondary' : 'primary'"
          v-for="dataLimitMode in dataLimitModes"
          :key="dataLimitMode"
          @click="$store.myActions.UI_SET_GRA_DATA({ uiid, name: 'dataLimit', value: dataLimitMode })"
        >
          {{ $t(`Pilot.Graphics.LimitData${dataLimitMode}`) }}
        </v-btn>
      </v-layout>
      <div style="flex: 1 0 auto; position: relative" class="mr-4">
        <Responsive
          :breakpoints="{
            small: el => el.width <= 420,
          }"
        >
          <v-layout
            slot-scope="el"
            @mousedown.prevent.stop="stopClick"
            class="scrollable-container"
            ref="detail"
            mt-2
            px-4
            row
            wrap
          >
            <v-flex
              v-for="g in graphesToPlot"
              style="position: relative"
              class="px-2 flex-grow-0"
              :class="{ sm12: el.is.small, sm6: !el.is.small }"
              :key="g.key"
            >
              <v-btn
                icon
                @click="selectGraph(g.fields)"
                small
                style="position: absolute; top: -7px; left: -2px; z-index: 1"
              >
                <v-icon>mdi-magnify-plus</v-icon>
              </v-btn>
              <div class="graphnmea mb-2">
                <line-chart
                  @onPointClick="onPointClick"
                  v-if="!hideGraph"
                  :chart-data="graphData(g.fields)"
                  :options="{
                    animation: { duration: 0 },
                    legend: { display: true, labels: { fontColor: '#000', boxWidth: 15, fontSize: 11 } },
                    responsive: true,
                    maintainAspectRatio: false,

                    scales: {
                      xAxes: [
                        {
                          type: 'time',
                          scaleLabel: { display: false, labelString: 'Date', padding: 0 },
                          time: {
                            parser: parseDateToUtc,
                          },
                        },
                      ],
                    },
                  }"
                  :width="300"
                  :height="200"
                ></line-chart>
                <div class="text-xs-center" v-else>
                  <v-progress-circular :size="70" :width="7" indeterminate></v-progress-circular>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </Responsive>
      </div>
    </template>
    <template v-else>
      <span class="display-2" style="margin: auto">{{ $t('Pilot.Nmea.NoDataAvailable') }}</span>
    </template>
  </div>
</template>

<script lang="ts">
import { DataPoolGlobalKeys } from '@/enum/DataPool'
import { NmeaConfig } from '@/models/datapool/NmeaConfig'
import { NmeaConfigField } from '@/models/datapool/NmeaConfigField'
import { NmeasConfig } from '@/models/datapool/NmeasConfig'
import LineChart from '@/components/charts/LineChart'
import Vue from 'vue'
//@ts-ignore
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { mustIgnoreValue } from '@/utils/autoscale'
import { scale } from 'ol/size'
import { head } from 'lodash'
import { ticks } from 'd3-array'
import { buildMissionUIStateId } from '@/models/ui/MissionUiState'
import moment, { duration, Moment } from 'moment'
import { chartColor } from '@/utils/autoscale'
import { groupBy } from '@/utils/groupBy'
import { dataLimitMode } from '@/models/ui/GraphUiState'

@Component({
  name: 'MissionDetailNmeaGraphicsList',
  components: { LineChart },
  props: {
    isleft: Boolean,
    uiid: {
      type: String,
      required: true,
    },
  },
})
export default class MissionDetailNmeaGraphicsList extends Vue {
  isleft!: boolean
  uiid!: string

  get selectedUi() {
    return this.isleft
      ? this.$store.myGetters.currentDetailMission.tabsleft.selected
      : this.$store.myGetters.currentDetailMission.tabsright.selected
  }

  hideGraph = false
  interval = undefined as undefined | number
  loadingData = true
  loadingValue = 0
  nmeasConfig = new NmeasConfig()
  nmeaLoading = {} as { [name: string]: boolean }

  get allGraphUiState() {
    return this.$store.myGetters.uistatebyuiid(this.uiid)
  }

  get dataLimit() {
    return this.allGraphUiState.dataLimit
  }

  dataLimitModes = [dataLimitMode.LAST_12H, dataLimitMode.LAST_24H, dataLimitMode.ALL]

  get originalNmeaConfigStr() {
    return (this.$store.myGetters.dataPoolItemsValueByKey(DataPoolGlobalKeys['global.config.nmea']) || '') as string
  }

  get mission() {
    return this.$store.myGetters.currentDetailMission.mission
  }

  get nmeas() {
    return [
      ...this.nmeasConfig.rowInit.filter(
        r => this.mission.nmeaTypeArray.findIndex(n => r.name.toLowerCase() == n.toLowerCase()) > -1,
      ),
      ...this.nmeasConfig.rowLazy.filter(
        r => this.mission.nmeaTypeArray.findIndex(n => r.name.toLowerCase() == n.toLowerCase()) > -1,
      ),
    ]
  }

  get plotVariables() {
    return this.nmeas.reduce((graphes, nmea) => {
      nmea.col1?.filter(d => d.canBePlot)?.forEach(d => graphes.push({ nmea: nmea.name, field: d }))
      nmea.col2?.filter(d => d.canBePlot)?.forEach(d => graphes.push({ nmea: nmea.name, field: d }))
      nmea.col3?.filter(d => d.canBePlot)?.forEach(d => graphes.push({ nmea: nmea.name, field: d }))
      return graphes
    }, [] as Array<{ nmea: string; field: NmeaConfigField }>)
  }

  get graphes() {
    return groupBy(
      this.plotVariables.filter(p => p.field.onMainPlot),
      p => (p.field.plotGroup.length > 0 ? p.field.plotGroup : `${p.nmea}_${p.field.name}`),
    )
  }

  get graphesToPlot() {
    return this.graphes.map(group => ({
      key: group.key,
      fields: group.values,
    }))
  }

  get minYos() {
    return Math.min(...this.mission.yoArray.map(y => y.yosCycle!))
  }

  get maxYos() {
    return Math.max(...this.mission.yoArray.map(y => y.yosCycle!))
  }

  get yoIndex() {
    return this.missionState && this.missionState.timelineIndex != -1
      ? this.missionState.timelineIndex
      : this.mission.yoArray.length - 1
  }

  get missionUiid() {
    return buildMissionUIStateId(this.mission.misId!, 'detail')
  }
  get missionState() {
    return this.$store.myGetters.uistatebyuiid(this.missionUiid)
  }

  get curYoIndex() {
    return this.missionState && this.missionState.timelineIndex != -1
      ? this.missionState.timelineIndex
      : this.mission.yoArray.length - 1
  }

  created() {
    this.loadingData = true
    this.$store.myActions.UI_ENSURE_ALL_GRA_STATE(this.uiid)
    this.$store.myActions.UI_SET_GRA_DATA({ uiid: this.uiid, name: 'dataLimit', value: dataLimitMode.ALL })
    this.$store.myActions.DATAPOOL_ENSURE({ fullKey: DataPoolGlobalKeys['global.config.nmea'] }).then(() => {
      this.nmeasConfig = NmeasConfig.createNmeasConfig(this.originalNmeaConfigStr)
      this.loadingData = false
      clearInterval(this.interval)
    })
  }

  @Watch('uiid')
  onUiidChanged(newValue: string) {
    this.$store.myActions.UI_ENSURE_ALL_GRA_STATE(newValue)
    this.$store.myActions.UI_SET_GRA_DATA({ uiid: newValue, name: 'dataLimit', value: dataLimitMode.ALL })
  }

  mounted() {
    if (this.loadingData) {
      this.interval = setInterval(() => {
        this.loadingValue = (this.loadingValue + 10) % 110
      }, 1000)
    }
  }

  @Watch('originalNmeaConfigStr')
  onOriginalNmeaConfigStrChange() {
    this.nmeasConfig = NmeasConfig.createNmeasConfig(this.originalNmeaConfigStr)
  }

  @Watch('nmeas')
  onNmeasChanged(newValues: Array<NmeaConfig>) {
    newValues.forEach(c => {
      if (c.name != 'computed') {
        this.$set(this.nmeaLoading, c.name, true)
        this.$store.myActions.MISSION_ENSURE_DATA_NMEA({ typeNmea: c.name, id: this.mission.misId! }).finally(() => {
          this.$set(this.nmeaLoading, c.name, false)
          //this.nmeaLoading[c.name] = false
        })
      }
    })
  }

  @Watch('curYoIndex')
  onCurYoIndexChange() {
    this.hideGraph = true
    this.$nextTick(() => (this.hideGraph = this.selectedUi != this.uiid))
  }

  @Watch('selectedUi')
  onSelectedUiChange() {
    this.$nextTick(() => (this.hideGraph = this.selectedUi != this.uiid))
  }

  stopClick() {}

  onPointClick(index: number) {
    this.$store.myActions.UI_SELECT_TIMELINE_MISSION_INDEX({
      index: this.graphToYoIndex[index],
      uiid: this.missionUiid,
    })
  }

  getNextYo(val: number, decalage: number = 1) {
    let yo = decalage == 0 ? this.mission.yoArray[val] : null
    let offset = 1
    //on prend la valeur absolue du décalage
    decalage = Math.abs(decalage)
    while (decalage > 0) {
      while (this.mission.yoArray.length > val + offset && (yo = this.mission.yoArray[val + offset]).isUnderWater) {
        offset++
      }
      decalage--
    }
    if (yo && !yo.isUnderWater) return yo.yosCycle
  }

  isInDateLimiteRange(date: Date) {
    if (this.dataLimit == dataLimitMode.ALL) return true
    const offsetH = 3600000 * (this.dataLimit == dataLimitMode.LAST_12H ? 12 : 24)
    //@ts-ignore
    const maxDateTime = this.mission.yoArray.at(-1)?.yoGpsDatTime.getTime()!
    return maxDateTime - date.getTime() <= offsetH
  }

  get graphToYoIndex() {
    return this.mission.yoArray
      .map((yo, index) => ({ yo, index }))
      .filter(y => !y.yo.isUnderWater && this.isInDateLimiteRange(y.yo.yoGpsDatTime))
      .map(y => y.index)
  }

  graphData(fields: Array<{ nmea: string; field: NmeaConfigField }>) {
    const graphDatas = {
      labels: this.mission.yoArray
        .filter(
          yo =>
            !yo.isUnderWater &&
            yo.yoGpsDatTime.toString() != 'Invalid Date' &&
            this.isInDateLimiteRange(yo.yoGpsDatTime),
        )
        .map(yo => yo.yoGpsDatTime),
      dataDic: fields?.reduce((dic, field) => {
        const dataNmea = this.mission.dataNmea[field.nmea!.toLowerCase()]
        if (!dataNmea?.dataSet) return dic
        const header = dataNmea?.columns?.find(h => h?.dbName?.toLowerCase() == field.field.name?.toLowerCase())
        if (header?.dbName?.endsWith('_LAT') || header?.dbName?.endsWith('_LON') || header?.dbTypeName == 'datetime2')
          return dic
        dic[`${field.nmea}_${field.field.name}`] = this.mission.yoArray
          .map((yo, index) => {
            const yoForData = this.getNextYo(index, field.field.offsetYoForPlot ?? 0)
            return {
              data: yo.isUnderWater
                ? null
                : yoForData == null ||
                  yoForData == undefined ||
                  dataNmea?.dataSet[yoForData] == undefined ||
                  mustIgnoreValue(dataNmea?.dataSet[yoForData][header?.pos!])
                ? null
                : dataNmea?.dataSet[yoForData!][header?.pos!] ?? null,
              yo,
            }
          })
          .filter(d => !d.yo.isUnderWater && this.isInDateLimiteRange(d.yo.yoGpsDatTime))
          .map(d => d.data)
        return dic
      }, {} as { [index: string]: any }),
    }

    return {
      labels: graphDatas.labels,
      datasets: fields
        .filter(field => (graphDatas.dataDic[`${field.nmea}_${field.field.name}`]?.length ?? 0) > 0)
        .map((field, index) => ({
          label: `${this.$i18n.locale == 'fr' ? field.field.libelleFr : field.field.libelleEn} ${
            field.field.unit ? `[${field.field.unit}]` : ''
          }`,
          backgroundColor: '#FFFFFF00',
          borderColor: chartColor[index],
          lineTension: 0,
          borderWidth: 1,
          pointRadius: (ctx: any) => {
            return this.graphToYoIndex[ctx.dataIndex] == this.curYoIndex ? 3 : 2
          },
          pointBorderColor: (ctx: any) => {
            return this.graphToYoIndex[ctx.dataIndex] == this.curYoIndex ? '#22b722' : chartColor[index]
          },
          pointBackgroundColor: (ctx: any) => {
            return this.graphToYoIndex[ctx.dataIndex] == this.curYoIndex ? '#22b722' : chartColor[index]
          },
          data: graphDatas.dataDic[`${field.nmea}_${field.field.name}`],
          spanGaps: true,
        })),
    }
  }

  selectGraph(fields: Array<{ nmea: string; field: NmeaConfigField }>) {
    const name = fields
      .map(
        f =>
          `${this.$i18n.locale == 'fr' ? f.field.libelleFr : f.field.libelleEn}${
            f.field.unit ? ` [${f.field.unit}]` : ''
          }`,
      )
      .join(' - ')

    this.$store.myActions
      //@ts-ignore
      .MISSION_NAVTO_DETAIL({ cmp: 'MissionDetailNmeaGraphics', isleft: !this.isleft, label: name })
      .then(() => {
        const tabsMission = !this.isleft
          ? this.$store.myGetters.currentDetailMission.tabsleft
          : this.$store.myGetters.currentDetailMission.tabsright
        //on attend que le composant soit créé
        this.$nextTick(() =>
          fields.forEach(f => this.$store.myActions.UI_SET_NMEA_GRA_CONFIG({ config: f, uiid: tabsMission.selected })),
        )
        this.$store.myActions.UI_SET_GRA_DATA({ uiid: tabsMission.selected, name: 'dataLimit', value: this.dataLimit })
      })
  }

  parseDateToUtc(data: any) {
    return moment(data).utc().format('YYYY-MM-DD HH:mm') //this.$options.filters['formatDate'](data,"YYYY-MM-DD HH:mm")
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
.graphnmea {
  border: 1px solid #ddd;
  box-sizing: content-box;
  width: 100%;
  height: 200px;
  position: relative;
  cursor: pointer;
}
</style>
